import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBell, faSync } from "@fortawesome/free-solid-svg-icons"
import { NavLink } from "react-router-dom"
import { COLORS } from "../../utilities/Constants"
import {
	markAllRead,
	getLastSeenId,
} from "../../store/actions/notificationActions"
import { reconnectNotificationsSocket } from "../../utilities/notificationsSocket"
import {
	NOTIFICATION_CATEGORIES,
	labelCategorie,
	lienNotification,
} from "../../config/notificationCategories"

// Bloc « centre de notifications » de la nav : indicateur de connexion temps
// réel, bouton reconnecter, badge des NON-TRAITÉS et dropdown groupé par
// catégorie. Affiché si le user est éligible à ≥ 1 catégorie (cf. NavV2 /
// config/notificationCategories.js). Le badge compte le travail en attente
// (items actifs) ; la surbrillance « nouveau » (high-water-mark localStorage)
// n'est qu'un confort visuel.
const styles = {
	wrap: { position: "relative", display: "flex", alignItems: "center" },
	bellBtn: {
		position: "relative",
		width: 34,
		height: 34,
		borderRadius: 9,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textMuted,
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 14,
		flexShrink: 0,
	},
	dot: (connected) => ({
		position: "absolute",
		bottom: -1,
		right: -1,
		width: 9,
		height: 9,
		borderRadius: 99,
		backgroundColor: connected ? COLORS.success : COLORS.danger,
		border: `2px solid ${COLORS.surface}`,
	}),
	badge: {
		position: "absolute",
		top: -5,
		right: -5,
		minWidth: 16,
		height: 16,
		padding: "0 4px",
		borderRadius: 99,
		backgroundColor: COLORS.danger,
		color: "#fff",
		fontSize: 10,
		fontWeight: 700,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		boxSizing: "border-box",
	},
	panelWrap: {
		position: "absolute",
		top: "100%",
		right: 0,
		paddingTop: 8,
		zIndex: 200,
	},
	panel: {
		width: 320,
		maxHeight: 420,
		overflowY: "auto",
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		padding: 8,
		boxShadow: "0 10px 30px rgba(28, 25, 23, 0.10)",
		fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "4px 6px 8px",
		borderBottom: `1px solid ${COLORS.border}`,
		marginBottom: 6,
	},
	headerTitle: { fontSize: 13, fontWeight: 700, color: COLORS.text },
	live: { fontSize: 12, fontWeight: 600, color: COLORS.success },
	reconnectBtn: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		padding: "4px 9px",
		borderRadius: 7,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 12,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	},
	section: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		padding: "8px 6px 2px",
		fontSize: 11,
		fontWeight: 700,
		color: COLORS.textMuted,
		textTransform: "uppercase",
		letterSpacing: 0.4,
	},
	sectionCount: {
		minWidth: 16,
		height: 16,
		padding: "0 4px",
		borderRadius: 99,
		backgroundColor: COLORS.accentLight,
		color: COLORS.accent,
		fontSize: 10,
		fontWeight: 700,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		boxSizing: "border-box",
	},
	item: (nouveau) => ({
		padding: "8px",
		borderRadius: 8,
		display: "flex",
		flexDirection: "column",
		gap: 2,
		backgroundColor: nouveau ? COLORS.accentLight : "transparent",
	}),
	itemHeader: {
		display: "flex",
		justifyContent: "space-between",
		fontSize: 11,
		color: COLORS.textLight,
	},
	itemCat: { fontSize: 13, fontWeight: 600, color: COLORS.text },
	itemTexte: { fontSize: 12, color: COLORS.textMuted },
	empty: {
		padding: "18px 8px",
		textAlign: "center",
		fontSize: 12,
		color: COLORS.textLight,
	},
	closeInfo: {
		padding: "0 6px 6px",
		fontSize: 11,
		color: COLORS.textLight,
	},
	footer: {
		borderTop: `1px solid ${COLORS.border}`,
		marginTop: 6,
		paddingTop: 6,
		textAlign: "center",
	},
	footerLink: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.accent,
		textDecoration: "none",
	},
}

const NavNotifications = () => {
	const dispatch = useDispatch()
	const { connected, connecting, items, unread, lastClose } = useSelector(
		(state) => state.notificationInfo,
	)
	const [open, setOpen] = useState(false)
	// Seuil de nouveauté figé À L'OUVERTURE du panneau : markAllRead avance le
	// high-water-mark immédiatement, mais la surbrillance doit rester visible
	// pendant que le panneau est ouvert.
	const [seuilNouveau, setSeuilNouveau] = useState(0)

	// Items groupés par catégorie, dans l'ordre du registre (catégories
	// inconnues — désync back/front — reléguées à la fin, jamais perdues).
	const sections = useMemo(() => {
		const parCat = {}
		items.forEach((it) => {
			const cle = it.type_notification || "signalement"
			if (!parCat[cle]) parCat[cle] = []
			parCat[cle].push(it)
		})
		const ordre = Object.keys(NOTIFICATION_CATEGORIES).filter((c) => parCat[c])
		Object.keys(parCat).forEach((c) => {
			if (!ordre.includes(c)) ordre.push(c)
		})
		return ordre.map((cle) => ({ cle, items: parCat[cle] }))
	}, [items])

	const toggle = () => {
		const next = !open
		setOpen(next)
		if (next) {
			setSeuilNouveau(getLastSeenId())
			// Ouvrir le panneau = marquer les nouveautés comme vues (surbrillance).
			// Le badge, lui, compte les NON-TRAITÉS : il ne se vide qu'en terminant.
			if (unread > 0) dispatch(markAllRead())
		}
	}

	return (
		<div style={styles.wrap} onMouseLeave={() => setOpen(false)}>
			<button
				type="button"
				style={styles.bellBtn}
				onClick={toggle}
				title={connected ? "Flux temps réel actif" : "Flux déconnecté"}
			>
				<FontAwesomeIcon icon={faBell} />
				<span style={styles.dot(connected)} />
				{items.length > 0 && (
					<span style={styles.badge}>
						{items.length > 99 ? "99+" : items.length}
					</span>
				)}
			</button>

			{open && (
				<div style={styles.panelWrap}>
					<div style={styles.panel}>
						<div style={styles.header}>
							<span style={styles.headerTitle}>Notifications</span>
							{connected ? (
								<span style={styles.live}>● En direct</span>
							) : (
								<button
									type="button"
									style={styles.reconnectBtn}
									onClick={reconnectNotificationsSocket}
								>
									<FontAwesomeIcon icon={faSync} spin={connecting} />
									Reconnecter
								</button>
							)}
						</div>

						{!connected && lastClose && (
							<div style={styles.closeInfo}>
								Dernière coupure : code {lastClose.code}
								{lastClose.reason ? ` — ${lastClose.reason}` : ""}
							</div>
						)}

						{items.length === 0 ? (
							<div style={styles.empty}>Aucune notification</div>
						) : (
							sections.map((section) => (
								<React.Fragment key={`section_${section.cle}`}>
									<div style={styles.section}>
										<span>{labelCategorie(section.cle)}</span>
										<span style={styles.sectionCount}>
											{section.items.length}
										</span>
									</div>
									{section.items.map((it) => (
										<div
											key={`notif_${it.id}`}
											style={styles.item(it.id > seuilNouveau)}
										>
											<div style={styles.itemHeader}>
												<span>{it.initiale}</span>
												<span>{it.created_at}</span>
											</div>
											{/* Signalement : sous-type libre saisi sur mobile.
											    Autres catégories : pas de sous-type, le texte suffit. */}
											{it.categorie ? (
												<span style={styles.itemCat}>{it.categorie}</span>
											) : null}
											<span style={styles.itemTexte}>{it.texte}</span>
											{lienNotification(it) && (
												<NavLink
													to={lienNotification(it)}
													style={{
														...styles.footerLink,
														alignSelf: "flex-start",
													}}
													onClick={() => setOpen(false)}
												>
													Ouvrir
												</NavLink>
											)}
										</div>
									))}
								</React.Fragment>
							))
						)}

						<div style={styles.footer}>
							<NavLink
								to="/notifications"
								style={styles.footerLink}
								onClick={() => setOpen(false)}
							>
								Voir tout
							</NavLink>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default NavNotifications
